/**
 * Directory Page
 *
 * Main directory page with search, filters, and instructor grid
 * Rendered inline by Dashboard for active users
 */

import { h } from 'preact';
import { useState } from 'preact/hooks';
import { useSearch } from '../../hooks/useSearch';
import { SearchBar } from '../../components/search/SearchBar';
import { Filters } from '../../components/search/Filters';
import { InstructorCard, InstructorCardSkeleton } from '../../components/instructor/InstructorCard';
import { Pagination } from '../../components/common/Pagination';

export function Directory() {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Use search hook for all search functionality
  const {
    instructors,
    total,
    page,
    perPage,
    totalPages,
    filters,
    loading,
    error,
    updateFilter,
    clearFilters,
    setPage,
    setPerPage
  } = useSearch();

  return (
    <div>
      {/* Page Header */}
      <div class="bg-white border-b border-gray-200 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-8">
        <div class="py-6">
          <h1 class="text-3xl font-bold text-gray-900">
            Directorio de Instructores
          </h1>
          <p class="mt-1 text-sm text-gray-500">
            Encuentra instructores certificados de Firewalking cerca de ti
          </p>

          {/* Search Bar */}
          <div class="mt-6">
            <SearchBar
              value={filters.query || ''}
              onChange={(value) => updateFilter('query', value)}
              placeholder="Buscar por nombre, especialidades..."
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div>
        <div class="lg:grid lg:grid-cols-12 lg:gap-8">
          {/* Sidebar - Filters */}
          <aside class="lg:col-span-3">
            {/* Mobile Filter Toggle */}
            <div class="lg:hidden mb-4">
              <button
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                class="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <span>Filtros</span>
                <svg
                  class={`w-5 h-5 transition-transform ${mobileFiltersOpen ? 'transform rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            {/* Filters Panel */}
            <div class={`${mobileFiltersOpen ? 'block' : 'hidden'} lg:block lg:sticky lg:top-8`}>
              <Filters
                filters={filters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                resultCount={total}
              />
            </div>
          </aside>

          {/* Main Content - Results */}
          <main class="lg:col-span-9">
            {/* Error State */}
            {error && (
              <div class="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div class="flex">
                  <svg
                    class="h-5 w-5 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div class="ml-3">
                    <p class="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div class="flex flex-col gap-4">
                {[...Array(6)].map((_, i) => (
                  <InstructorCardSkeleton key={i} />
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && instructors.length === 0 && (
              <div class="text-center py-12">
                <svg
                  class="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <h3 class="mt-2 text-sm font-medium text-gray-900">No se encontraron instructores</h3>
                <p class="mt-1 text-sm text-gray-500">
                  Intenta ajustar tus filtros o términos de búsqueda
                </p>
                {Object.keys(filters).length > 0 && (
                  <button
                    onClick={clearFilters}
                    class="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            )}

            {/* Results List (Full Width) */}
            {!loading && !error && instructors.length > 0 && (
              <>
                <div class="flex flex-col gap-4">
                  {instructors.map((instructor) => (
                    <InstructorCard key={instructor.id} instructor={instructor} />
                  ))}
                </div>

                {/* Pagination */}
                <div class="mt-8">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalResults={total}
                    perPage={perPage}
                    onPageChange={setPage}
                    onPerPageChange={setPerPage}
                  />
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
